import type { AccessRequestView } from "@esi/shared";
import type { TFunction } from "i18next";
import { ApiError } from "../api-errors.ts";
import { t as translate } from "../i18n/i18n.ts";

/**
 * A governance answer of `202` (openspec change `governance-access-approval`): nothing was
 * applied, the change waits for the project's approval. Thrown as an `ApiError` with code
 * `pending_approval`, so dialogs stay honest (no "Saved") and say so in plain words; the
 * pending notice then comes from the requester's own request list.
 */
export function rejectPending<R extends { status: number }>(res: R): R {
  if (res.status === 202) {
    throw new ApiError(202, translate("governance.pending.sent"), "pending_approval", true);
  }
  return res;
}

export const isPendingApproval = (error: unknown) =>
  error instanceof ApiError && error.code === "pending_approval";

/** The requested change in plain words, for the requester and the deciders alike. */
export function describeChange(request: AccessRequestView, t: TFunction): string {
  const { change } = request;
  const name = request.subjectName ?? t("governance.change.unknown");
  const role = (r: string) => t(`projectRoles.${r}.name` as "projectRoles.reader.name");
  const scope = request.subjectName ?? t("governance.change.wholeTenant");
  switch (change.kind) {
    case "project.update":
      return change.input.visibility
        ? t("governance.change.visibility", {
            visibility: t(`project.visibility.${change.input.visibility}`),
          })
        : t("governance.change.details");
    case "project.member_add":
      return t("governance.change.memberAdd", { name, role: role(change.role) });
    case "project.group_add":
      return t("governance.change.groupAdd", { name, role: role(change.role) });
    case "project.group_role":
      return t("governance.change.groupRole", { name, role: role(change.role) });
    case "project.rule_grant":
      return t("governance.change.ruleGrant", { name, role: role(change.role) });
    case "project.setting":
      return t("governance.change.setting", { key: change.key });
    case "project.transfer":
      return t("governance.change.transfer", { name });
    case "entity.member_add":
      return t("governance.change.entityJoin", { name });
    case "setting.put":
      return change.key === "projects.shareOutsideEntity"
        ? t("governance.change.shareOutside", { scope })
        : t("governance.change.setting", { key: change.key });
    case "setting.reset":
      return change.key === "projects.shareOutsideEntity"
        ? t("governance.change.shareOutsideReset", { scope })
        : t("governance.change.settingReset", { key: change.key, scope });
    case "access.entity_move":
      return t("governance.change.entityMove", { name, role: role(change.role) });
    case "access.directory":
      return t("governance.change.directorySync", { role: role(change.role) });
  }
}
